// Web push (Firebase Cloud Messaging) — permission prompt, device token
// retrieval, and the foreground-message listener. Background messages (no
// ARC Beacon tab focused) are handled separately by
// public/firebase-messaging-sw.js, which can't share this module since
// service workers can't import from src/.
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let messagingPromise = null;

// isSupported() rules out Safari/older browsers and non-secure contexts
// (the Push API needs HTTPS, localhost is exempted) — without this guard,
// getMessaging() throws instead of just declining.
function getMessagingInstance() {
  if (!messagingPromise) {
    messagingPromise = isSupported().then((ok) =>
      ok ? getMessaging(initializeApp(firebaseConfig)) : null
    );
  }
  return messagingPromise;
}

// Prompts for notification permission (no-op if already granted/denied by
// the user previously) and returns this device's FCM token, or null if
// permission was refused or push isn't supported here.
export async function requestPushToken() {
  if (!firebaseConfig.apiKey) return null; // env not configured yet

  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;

  await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  // On a first-ever registration the worker is still "installing" at this
  // point — subscribing before it reaches "active" throws ("no active
  // Service Worker"). `.ready` resolves only once an active worker exists.
  const registration = await navigator.serviceWorker.ready;
  return (
    (await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    })) || null
  );
}

// Fires `handler(payload)` for pushes that arrive while a tab is open and
// focused. Returns an unsubscribe function.
//
// This listens for a message the service worker relays itself (see
// public/firebase-messaging-sw.js) rather than using Firebase's onMessage()
// — that relies on Firebase's own SW-side wrapper, which this app's service
// worker deliberately doesn't use (see the comment at the top of that file
// for why). Both sides of this relay must stay in sync: the SW decides
// "tab is focused" and posts { type: "ARC_PUSH_MESSAGE", payload }; this
// only needs to listen for that exact type.
export async function onForegroundPush(handler) {
  if (!("serviceWorker" in navigator)) return () => {};

  const listener = (event) => {
    if (event.data?.type === "ARC_PUSH_MESSAGE") handler(event.data.payload);
  };
  navigator.serviceWorker.addEventListener("message", listener);
  return () => navigator.serviceWorker.removeEventListener("message", listener);
}
